import { Badge, Box, Theme, Typography } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import { AyceCampaignDto } from 'apis/ayceCampaign';
import { fetchCatalog } from 'application/discounts/promotionCatalogSlice';
import { Loader } from 'components/common/Loader/Loader';
import { VendorCheckboxPicker, VendorPickedTabs, useVendorPicker } from 'components/common/VendorPicker';
import { isLoading, isNotRequested } from 'helpers/requestStateHelper';
import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'store';
import VendorAyceCampaign, { VendorCampaignHandlers } from '../VendorAyce/VendorAyceCampaign';
import AyceCampaignForm, { AyceCampaignFormHandlers } from './AyceCampaignForm';

type CampaignEditorProps = {
    campaign?: AyceCampaignDto;
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        wrapper: { margin: '44.5px' },
        header: {
            fontSize: '2rem',
        },
        tab_draft: {
            '& .MuiBadge-badge': {
                backgroundColor: theme.palette.warning.dark,
            },
        },
        tab_published: {
            '& .MuiBadge-badge': {
                backgroundColor: theme.palette.success.dark,
            },
        },
        tab_unpublished: {
            '& .MuiBadge-badge': {
                backgroundColor: theme.palette.error.dark,
            },
        },
    }),
);

export default function CampaignEditor({ campaign }: CampaignEditorProps): React.ReactElement {
    const classes = useStyles();
    const dispatch = useDispatch();
    const vendorPickerState = useVendorPicker();
    const { allVendors, currentVendor, setPickedVendors } = vendorPickerState;

    const vendors = useSelector(state => state.apiConfig.vendors);
    const catalog = useSelector(state => state.promotionCatalog);
    const [initialCampaign, setInitialCampaign] = useState<AyceCampaignDto | null>(null);

    const campaignRef = useRef<AyceCampaignFormHandlers>();
    const vendorCampaignRef = useRef<VendorCampaignHandlers>();

    const onSubmit = async () => {
        const [campaignResult, vendorCampaign] = await Promise.all([
            campaignRef.current?.submit(),
            vendorCampaignRef.current?.submit(),
        ]);

        console.log('campaignResult', campaignResult);
        console.log('vendorCampaign', vendorCampaign);

        if (!campaignResult || !vendorCampaign) return;

        // save
    };

    useEffect(() => {
        if (!campaign) return;
        setInitialCampaign(campaign);
        setPickedVendors(
            allVendors.filter(vendor => campaign.vendorCampaignList.find(item => item.vendorName === vendor.name)),
        );
    }, [campaign, allVendors]);

    useEffect(() => {
        if (isNotRequested(catalog)) dispatch(fetchCatalog());
    }, []);

    const tabLabels = useMemo(() => {
        if (!campaign) return {};
        return campaign.vendorCampaignList.reduce(
            (acc, { vendorName, status }) => ({
                ...acc,
                [vendorName]: (
                    <Badge className={classes['tab_' + status]} variant="dot" overlap="rectangular">
                        {vendorName}
                    </Badge>
                ),
            }),
            {} as { [vendorName: string]: React.ReactNode },
        );
    }, [campaign]);

    if (isLoading(vendors) || isLoading(catalog)) {
        return <Loader />;
    }

    return (
        <Box className={classes.wrapper} data-cy="Ayce-Campaign-Editor">
            <Typography variant="subtitle1" component="h1" className={classes.header}>
                Ayce Campaign Editor
            </Typography>
            <Box>
                <AyceCampaignForm ref={campaignRef} campaign={initialCampaign}>
                    <VendorCheckboxPicker
                        isDisabled={(vendorName: string) =>
                            !!campaign?.vendorCampaignList.find(v => v.vendorName === vendorName)
                        }
                        cy="Ayce-Vendor-Checkbox-"
                        {...vendorPickerState}
                    />
                </AyceCampaignForm>

                {currentVendor && (
                    <VendorPickedTabs {...vendorPickerState} customLabels={tabLabels}>
                        <VendorAyceCampaign
                            ref={vendorCampaignRef}
                            vendor={currentVendor}
                            vendorCampaign={initialCampaign?.vendorCampaignList.find(
                                c => c.vendorName === currentVendor.name,
                            )}
                            onSubmit={onSubmit}
                        />
                    </VendorPickedTabs>
                )}
            </Box>
        </Box>
    );
}
